import { useCallback, useEffect, useMemo, useState } from "react";
import "bootstrap-icons/font/bootstrap-icons.css";
import { getControls } from "../api";
import ServerSideDataTable from "./ServerSideDataTable";

const DEFAULT_SORT_BY = "report_created_at";
const DEFAULT_SORT_INDEX = 2;

const FILTER_BADGES = [
    { key: "geometry",       label: "Géométrie",          testTypeId: 1 },
    { key: "tire_structure", label: "Structure de pneu",  testTypeId: 2 },
];

// Clés valides pour éviter de polluer l'URL avec des valeurs arbitraires
const VALID_FILTER_KEYS = new Set(FILTER_BADGES.map((f) => f.key));
const VALID_FILTER_IDS = new Set(FILTER_BADGES.map((f) => f.testTypeId));

const STORAGE_KEY = "test_types_filter";

// lecture initiale depuis l'URL
function readFiltersFromUrl() {
    const params = new URLSearchParams(window.location.search);
    const raw = params.get("filters");
    if (!raw) return null;
    return raw.split(",").filter((k) => VALID_FILTER_KEYS.has(k));
}

function readFiltersFromStorage() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return [];
        return parsed.filter((k) => VALID_FILTER_KEYS.has(k));
    } catch {
        return [];
    }
}

// Priorité : URL (si présente) > localStorage > []
function readInitialFilters() {
    const fromUrl = readFiltersFromUrl();
    if (fromUrl !== null) return fromUrl;
    return readFiltersFromStorage() ?? [];
}

function ControlsDataTable({ shopId = null, hideShopColumns = false, filtersWhitelist = null }) {
    const [activeFilters, setActiveFilters] = useState(readInitialFilters());

    const toggleFilter = useCallback((key) => {
        setActiveFilters((prev) =>
            prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
        );
    }, []);

    useEffect(() => {
        // URL
        const params = new URLSearchParams(window.location.search);
        if (activeFilters.length > 0) {
            params.set("filters", activeFilters.join(","));
        } else {
            params.delete("filters");
        }
        const next = `${window.location.pathname}?${params.toString()}${window.location.hash}`;
        window.history.replaceState(null, "", next);

        // localStorage
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(activeFilters));
        } catch (err) {
            // quota dépassé ou mode privé restrictif — on ignore silencieusement
        }
    }, [activeFilters]);

    // Dérive testTypeId depuis la sélection :
    // - aucun ou les deux sélectionnés → null (pas de filtre)
    // - un seul → testTypeId correspondant
    const testTypeId = useMemo(() => {
        const active = FILTER_BADGES.filter((f) => activeFilters?.includes(f.key));
        return active.length === 1 ? active[0].testTypeId : null;
    }, [activeFilters]);

    const handleShareReport = useCallback(async (button, setError) => {
        const encodedUrl = button.getAttribute("data-copy-url");
        if (!encodedUrl) {
            return;
        }

        const Tooltip = window.bootstrap?.Tooltip;
        const tooltipInstance = Tooltip ? Tooltip.getOrCreateInstance(button) : null;

        try {
            await navigator.clipboard.writeText(decodeURIComponent(encodedUrl));

            const previousTitle = button.getAttribute("title") || "Copier le lien";
            button.classList.remove("btn-outline-secondary");
            button.classList.add("btn-success");
            button.setAttribute("title", "Lien copié");
            button.setAttribute("data-bs-original-title", "Lien copié");
            tooltipInstance?.setContent?.({ ".tooltip-inner": "Lien copié" });
            tooltipInstance?.show?.();

            setTimeout(() => {
                button.classList.remove("btn-success");
                button.classList.add("btn-outline-secondary");
                if (previousTitle) {
                    button.setAttribute("title", previousTitle);
                    button.setAttribute("data-bs-original-title", previousTitle);
                    tooltipInstance?.setContent?.({ ".tooltip-inner": previousTitle });
                }
            }, 1200);
        } catch {
            setError("Impossible de copier le lien.");
        }
    }, []);

    const registerDomHandlers = useCallback(({ wrapper, setError }) => {
        const onClick = async (event) => {
            const button = event.target.closest("[data-copy-url]");
            if (!button || !wrapper.contains(button)) {
                return;
            }

            event.preventDefault();
            await handleShareReport(button, setError);
        };

        wrapper.addEventListener("click", onClick);
        return () => {
            wrapper.removeEventListener("click", onClick);
        };
    }, [handleShareReport]);

    const columns = useMemo(() => {
        const baseColumns = [
        { data: "id", title: "ID" },
        {
            data: "test_type_id",
            title: "Contrôle",
            render: (data) => {
                const badge = FILTER_BADGES.find((f) => f.testTypeId === data);
                return `
                    <span class="badge rounded-pill border-0 fs-6 fw-normal px-3 py-2 bg-body-secondary text-body">${badge ? badge.label : "-"}</span>
                `;
            }
        },
        {
            data: "report_id",
            title: "Rapport",
            render: (data, type, row) => {
                if (type !== "display") {
                    return data || "";
                }

                if (row?.report_download_url) {
                    const reportId = data || "-";
                    const encodedDownloadUrl = encodeURIComponent(row.report_download_url);

                    return `
                        <div class="btn-group btn-group-sm" role="group" aria-label="Actions rapport">
                            <a
                                href="${row.report_download_url}"
                                target="_blank"
                                rel="noopener noreferrer"
                                class="btn btn-primary d-inline-flex align-items-center gap-1"
                            >
                                <i class="bi bi-download" aria-hidden="true"></i>
                                <span>${reportId}</span>
                            </a>
                            <button
                                type="button"
                                class="btn btn-outline-secondary"
                                title="Copier le lien"
                                data-bs-toggle="tooltip"
                                data-bs-placement="top"
                                aria-label="Copier le lien du rapport"
                                data-copy-url="${encodedDownloadUrl}"
                            >
                                <i class="bi bi-share" aria-hidden="true"></i>
                            </button>
                        </div>
                    `;
                }

                return data || "-";
            }
        },
        {
            data: "report_created_at",
            title: "Date",
            render: (data, type) => {
                if (type !== "display") {
                    return data || "";
                }

                return data ? new Date(data).toLocaleString() : "-";
            }
        },
        {
            data: null,
            title: "Véhicule",
            render: (_, type, row) => {
                const display = [row?.car_brand, row?.car_model].filter(Boolean).join(" ") || "-";
                return type === "display" ? display : [row?.car_brand, row?.car_model].filter(Boolean).join(" ");
            }
        }
        ];

        if (!hideShopColumns) {
            baseColumns.push(
                {
                    data: "shop_name",
                    title: "Nom de l'atelier",
                    render: (data) => data || "-"
                },
                {
                    data: "shop_email",
                    title: "Email de l'atelier",
                    render: (data, type, row) => {
                        if (!data) {
                            return "-";
                        }

                        if (type !== "display") {
                            return data;
                        }

                        if (row?.shop_id) {
                            return `<a href="/shops/${row.shop_id}" class="text-decoration-none">${data}</a>`;
                        }

                        return data;
                    }
                }
            );
        }

        return baseColumns;
    }, [hideShopColumns]);

    return (
        <div>
            <div className="d-flex flex-wrap gap-2 mb-3">
                {FILTER_BADGES.map(({ key, label, testTypeId }) => {
                    if (filtersWhitelist && !filtersWhitelist.includes(testTypeId)) {
                        return (
                            <button
                                key={key}
                                type="button"
                                className={`badge rounded-pill border-0 fs-6 fw-normal px-3 py-2 bg-dark-subtle text-body-secondary`}
                                style={{ cursor: "default" }}
                            >
                                {label}
                            </button>
                        );
                    }

                    const active = activeFilters.includes(key);
                    return (
                        <button
                            key={key}
                            type="button"
                            onClick={() => toggleFilter(key)}
                            aria-pressed={active}
                            className={`badge rounded-pill border-0 fs-6 fw-normal px-3 py-2 ${
                                active
                                    ? "bg-primary text-white"
                                    : "bg-body-secondary text-body"
                            }`}
                            style={{ cursor: "pointer" }}
                        >
                            {active && <i className="bi bi-check me-1" aria-hidden="true" />}
                            {label}
                        </button>
                    );
                })}
            </div>

            {/*
                key={testTypeId} : quand testTypeId change, React démonte et remonte
                ServerSideDataTable, ce qui force DataTables à refaire un ajax
                avec les nouveaux params — sans toucher à ServerSideDataTable.
            */}
            <ServerSideDataTable
                key={testTypeId}
                columns={columns}
                defaultSortBy={DEFAULT_SORT_BY}
                defaultSortIndex={DEFAULT_SORT_INDEX}
                errorMessage="Impossible de charger les contrôles pour l'instant."
                registerDomHandlers={registerDomHandlers}
                fetchPage={(token, query) => getControls(token, query.page, query.pageSize, {
                    q: query.q,
                    sortBy: query.sortBy,
                    sortDir: query.sortDir,
                    shopId,
                    // --- NOUVEAU : injecté dans la requête, absent si null ---
                    ...(testTypeId !== null && { testTypeId })
                })}
            />
        </div>
    );
}

export default ControlsDataTable;
